import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, useFieldArray, Controller, type SubmitHandler } from 'react-hook-form';
import { getInvoice, patchInvoice, deleteInvoice, reprocessInvoice, exportInvoice, exportInvoicesZip, downloadBlob, fetchOriginalFileBlob, finalizeInvoice } from '@/lib/invoices';
import { PdfCanvas } from '@/components/preview/PdfCanvas';
import {
  Button, Card, Input, Modal, Checkbox, Badge, Alert, toast,
  Icon, DropdownSelect, DatePicker, MacTitlebar,
  type DropdownOption,
  type IconName,
} from '@/components/ui';
import { en } from '@/i18n/en';
import { EXPORT_FORMATS, type ExportFormat, type Invoice, type VerificationResult } from '@/types/invoice';
import { formatMoney } from '@/lib/format';

interface FormShape {
  vendor: Invoice['vendor'];
  buyer: Invoice['buyer'];
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  currency: string;
  lineItems: Invoice['lineItems'];
  subtotal: number;
  taxTotal: number;
  total: number;
  paymentTerms: string;
  notes: string;
}

function emptyParty() { return { name: '', address: '', taxId: '', email: '', phone: '' }; }

const CURRENCY_OPTIONS: DropdownOption[] = [
  { value: 'USD', label: 'USD', hint: 'US Dollar' },
  { value: 'EUR', label: 'EUR', hint: 'Euro' },
  { value: 'GBP', label: 'GBP', hint: 'British Pound' },
  { value: 'MAD', label: 'MAD', hint: 'Moroccan Dirham' },
  { value: 'JPY', label: 'JPY', hint: 'Japanese Yen' },
  { value: 'CHF', label: 'CHF', hint: 'Swiss Franc' },
  { value: 'CAD', label: 'CAD', hint: 'Canadian Dollar' },
  { value: 'AUD', label: 'AUD', hint: 'Australian Dollar' },
  { value: 'CNY', label: 'CNY', hint: 'Chinese Yuan' },
  { value: 'INR', label: 'INR', hint: 'Indian Rupee' },
];

export function InvoiceDetailPage() {
  const t = en.dashboard.detail;
  const { id = '' } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [exportOpen, setExportOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const query = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => getInvoice(id),
    refetchInterval: (q) => {
      const inv = q.state.data as Invoice | undefined;
      return inv?.status === 'processing' ? 1500 : false;
    },
  });

  const { register, control, handleSubmit, reset, formState: { isDirty } } = useForm<FormShape>({
    defaultValues: {
      vendor: emptyParty(), buyer: emptyParty(),
      invoiceNumber: '', issueDate: '', dueDate: '', currency: 'USD',
      lineItems: [], subtotal: 0, taxTotal: 0, total: 0,
      paymentTerms: '', notes: '',
    },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'lineItems' });

  // The form works in DOLLARS for human-readable editing; the wire format is
  // still minor units (cents). Convert when loading from / saving to the API.
  const toDollars = (cents: number): number => Math.round(cents) / 100;
  const toCents = (dollars: number): number => Math.round((Number(dollars) || 0) * 100);
  const toRatePercent = (basisPoints: number): number => Math.round(basisPoints) / 100;
  const toRateBasisPoints = (percent: number): number => Math.round((Number(percent) || 0) * 100);

  useEffect(() => {
    if (query.data) {
      const d = query.data;
      reset({
        vendor: d.vendor, buyer: d.buyer,
        invoiceNumber: d.invoiceNumber,
        issueDate: d.issueDate?.slice(0, 10) ?? '',
        dueDate: d.dueDate?.slice(0, 10) ?? '',
        currency: d.currency,
        lineItems: d.lineItems.map((li) => ({
          ...li,
          unitPrice: toDollars(li.unitPrice),
          taxRate: toRatePercent(li.taxRate),
          total: toDollars(li.total),
        })),
        subtotal: toDollars(d.subtotal),
        taxTotal: toDollars(d.taxTotal),
        total: toDollars(d.total),
        paymentTerms: d.paymentTerms ?? '', notes: d.notes ?? '',
      });
    }
  }, [query.data, reset]);

  const save = useMutation({
    mutationFn: (patch: Partial<Invoice>) => patchInvoice(id, patch),
    onSuccess: (updated) => {
      qc.setQueryData(['invoice', id], updated);
      toast('Facture enregistrée.', 'success');
    },
    onError: (e: unknown) => {
      toast(e instanceof Error ? e.message : 'Échec de l\'enregistrement', 'danger');
    },
  });

  const reprocess = useMutation({
    mutationFn: () => reprocessInvoice(id),
    onSuccess: (updated) => {
      qc.setQueryData(['invoice', id], updated);
      toast('Relance OCR en file d\'attente.', 'info');
    },
  });

  const finalize = useMutation({
    mutationFn: () => finalizeInvoice(id),
    onSuccess: (updated) => {
      qc.setQueryData(['invoice', id], updated);
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      toast('Facture finalisée.', 'success');
    },
    onError: (e: unknown) => toast(e instanceof Error ? e.message : 'Échec de la finalisation', 'danger'),
  });

  const del = useMutation({
    mutationFn: () => deleteInvoice(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      toast('Facture supprimée.', 'info');
      nav('/dashboard');
    },
  });

  const onSubmit: SubmitHandler<FormShape> = (v) => {
    save.mutate({
      ...v,
      dueDate: v.dueDate || null,
      paymentTerms: v.paymentTerms || null,
      notes: v.notes || null,
      subtotal: toCents(v.subtotal),
      taxTotal: toCents(v.taxTotal),
      total: toCents(v.total),
      lineItems: v.lineItems.map((li) => ({
        ...li,
        unitPrice: toCents(li.unitPrice),
        taxRate: toRateBasisPoints(li.taxRate),
        total: toCents(li.total),
      })),
    });
  };

  if (query.isLoading) return <div className="empty-state">Chargement…</div>;
  if (query.error || !query.data) return <Alert tone="danger" title="Impossible de charger la facture.">{(query.error as Error | undefined)?.message ?? 'Inconnu'}</Alert>;

  const inv = query.data;
  const isProcessing = inv.status === 'processing';
  const isFailed = inv.status === 'failed';

  return (
    <div className="stack-lg">
      <div className="row-between">
        <div>
          <Link to="/dashboard" className="lnk-ghost" style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Icon name="chevron-left" size={12} /> Factures
          </Link>
          <h1 style={{ margin: '6px 0 0', fontSize: 26, letterSpacing: '-0.025em' }}>
            {t.title} {inv.invoiceNumber && <span className="mono muted" style={{ fontSize: 14 }}>· {inv.invoiceNumber}</span>}
          </h1>
        </div>
        <div className="row-gap">
          <Badge variant={inv.status} />
          {inv.origin !== 'created' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => reprocess.mutate()}
              disabled={isProcessing || reprocess.isPending}
              title={isProcessing ? 'Traitement déjà en cours' : undefined}
              leadingIcon={<Icon name="scan" size={14} />}
            >
              {isProcessing ? 'Traitement…' : t.reprocess}
            </Button>
          )}
          {inv.status === 'draft' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => finalize.mutate()}
              disabled={finalize.isPending}
              leadingIcon={<Icon name="check" size={14} />}
              title="Marquer comme prête et retirer des brouillons"
            >
              {finalize.isPending ? '…' : 'Finaliser'}
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={() => setExportOpen(true)}
            disabled={inv.status !== 'ready' && inv.status !== 'edited' && inv.status !== 'draft'}
            leadingIcon={<Icon name="download" size={14} />}
          >
            {t.export}
          </Button>
          <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)} leadingIcon={<Icon name="x" size={14} />}>
            Supprimer
          </Button>
        </div>
      </div>

      <WorkflowSteps invoice={inv} />

      {isProcessing && <Alert tone="info" title="Traitement en cours">L'analyse OCR s'exécute. Cette vue s'actualisera automatiquement.</Alert>}
      {isFailed && <Alert tone="danger" title="Échec de l'OCR">{inv.raw?.ocrText || 'Le service OCR a rencontré une erreur. Vous pouvez relancer l\'analyse.'}</Alert>}

      <VerificationCard verification={inv.raw?.verification} />


      <div className="detail-grid">
        <Card>
          <form onSubmit={handleSubmit(onSubmit)} className="stack">
            <Section title={t.sections.invoice}>
              <Input label="Numéro de facture" {...register('invoiceNumber')} />
              <Controller
                name="issueDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Date d'émission"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                name="dueDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Date d'échéance"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <DropdownSelect
                    label="Devise"
                    value={field.value ?? 'EUR'}
                    onChange={field.onChange}
                    options={CURRENCY_OPTIONS}
                  />
                )}
              />
            </Section>

            <Section title={t.sections.vendor}>
              <Input label="Nom ou Raison sociale" {...register('vendor.name')} />
              <Input label="Adresse" {...register('vendor.address')} />
              <Input label="N° TVA / SIRET" {...register('vendor.taxId')} />
              <Input label="E-mail" type="email" {...register('vendor.email')} />
              <Input label="Téléphone" {...register('vendor.phone')} />
            </Section>

            <Section title={t.sections.buyer}>
              <Input label="Nom ou Entreprise" {...register('buyer.name')} />
              <Input label="Adresse" {...register('buyer.address')} />
              <Input label="N° TVA / SIRET" {...register('buyer.taxId')} />
              <Input label="E-mail" type="email" {...register('buyer.email')} />
              <Input label="Téléphone" {...register('buyer.phone')} />
            </Section>

            <Section title={t.sections.items} full>
              <div className="table-responsive">
                <table className="line-items-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th style={{ width: 80 }}>Qté</th>
                      <th style={{ width: 110 }}>Prix unitaire</th>
                      <th style={{ width: 80 }}>TVA %</th>
                      <th style={{ width: 120 }} className="right">Total HT</th>
                      <th style={{ width: 30 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((f, idx) => (
                      <tr key={f.id}>
                        <td><input className="input" {...register(`lineItems.${idx}.description` as const)} /></td>
                        <td><input type="number" step="0.01" className="input" {...register(`lineItems.${idx}.quantity` as const, { valueAsNumber: true })} /></td>
                        <td><input type="number" step="0.01" className="input" {...register(`lineItems.${idx}.unitPrice` as const, { valueAsNumber: true })} /></td>
                        <td><input type="number" step="0.01" className="input" {...register(`lineItems.${idx}.taxRate` as const, { valueAsNumber: true })} /></td>
                        <td className="amount right"><input type="number" step="0.01" className="input" {...register(`lineItems.${idx}.total` as const, { valueAsNumber: true })} /></td>
                        <td><button type="button" className="row-action" aria-label={t.removeLine} onClick={() => remove(idx)}>×</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button type="button" size="sm" variant="ghost" onClick={() => append({ description: '', quantity: 1, unitPrice: 0, taxRate: 0, total: 0 })}>+ {t.addLine}</Button>
            </Section>

            <Section title={t.sections.totals}>
              <Input type="number" step="0.01" label={`Sous-total HT (${inv.currency})`} {...register('subtotal', { valueAsNumber: true })} />
              <Input type="number" step="0.01" label={`Total TVA (${inv.currency})`} {...register('taxTotal', { valueAsNumber: true })} />
              <Input type="number" step="0.01" label={`Total TTC (${inv.currency})`} {...register('total', { valueAsNumber: true })} />
            </Section>

            <Section title={t.sections.notes} full>
              <Input label="Conditions de règlement" {...register('paymentTerms')} />
              <Input label="Notes" {...register('notes')} />
            </Section>

            <div className="row-between" style={{ marginTop: 4 }}>
              <span className="muted">Extrait par : <strong>{inv.raw.extractor || 'n/a'}</strong> · indice de confiance {(inv.raw.confidence * 100).toFixed(0)}%</span>
              <Button type="submit" variant="primary" disabled={!isDirty || save.isPending}>{save.isPending ? '…' : en.common.save}</Button>
            </div>
          </form>
        </Card>

        <FilePreview invoice={inv} />
      </div>

      <ExportModal open={exportOpen} onClose={() => setExportOpen(false)} invoiceId={inv.id} />
      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Supprimer cette facture ?"
        lede="Les données structurées et le fichier original téléversé seront définitivement supprimés."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>{en.common.cancel}</Button>
            <Button variant="destructive" onClick={() => del.mutate()} disabled={del.isPending}>Supprimer</Button>
          </>
        }
      >
        <div />
      </Modal>
    </div>
  );
}

type StepState = 'pending' | 'active' | 'done' | 'failed';
interface WorkflowStep { key: string; label: string; sub: string; icon: IconName; state: StepState; }

/**
 * Tiny pipeline visualization that mirrors what the backend is actually doing
 * for this invoice. Without per-stage signals from the API we infer the state
 * from `status`, `source.uploadedAt`, and `raw.extractor`.
 */
function WorkflowSteps({ invoice }: { invoice: Invoice }) {
  const status = invoice.status;
  const extractor = invoice.raw?.extractor ?? '';
  const isProcessing = status === 'processing';
  const isReady = status === 'ready' || status === 'edited';
  const isFailed = status === 'failed';
  // If extraction returned with a real OCR engine name (not 'error'), text-OCR
  // succeeded — any subsequent failure is in the structured-field step.
  const hasOcrText = !!extractor && extractor !== 'error';
  const provider = extractor.includes('ollama:') ? 'Ollama' : extractor.includes('tesseract') || extractor.includes('pdfplumber') ? 'Tesseract' : '';

  const steps: WorkflowStep[] = [
    {
      key: 'upload',
      label: 'Fichier téléversé',
      sub: invoice.source?.filename ?? '',
      icon: 'upload',
      state: 'done',
    },
    {
      key: 'ocr',
      label: 'Reconnaissance OCR',
      sub: isReady ? (provider || 'Moteur OCR') : isProcessing ? 'En cours…' : isFailed ? (hasOcrText ? 'Terminé' : 'Échoué') : 'En attente',
      icon: 'scan',
      state: isReady ? 'done' : isFailed ? (hasOcrText ? 'done' : 'failed') : isProcessing ? 'active' : 'pending',
    },
    {
      key: 'extract',
      label: 'Extraction des champs',
      sub: isReady
        ? `${invoice.lineItems.length} ligne${invoice.lineItems.length > 1 ? 's' : ''}`
        : isProcessing ? 'En attente après OCR'
        : isFailed ? (hasOcrText ? 'Échoué' : 'Non démarré')
        : 'En attente',
      icon: 'sparkle',
      // Don't claim both OCR and Extract are running simultaneously — the
      // backend doesn't expose per-stage signals, so present them as a clear
      // sequence: Extract stays pending while status === 'processing', then
      // flips to done together with OCR when status becomes ready.
      state: isReady ? 'done' : isFailed && hasOcrText ? 'failed' : 'pending',
    },
    {
      key: 'ready',
      label: status === 'edited' ? 'Enregistrée (modifiée)' : 'Prête à l\'export',
      sub: isReady ? 'Vous pouvez exporter ou corriger les champs' : isFailed ? 'Relancez l\'OCR pour réessayer' : 'En attente',
      icon: 'check',
      state: isReady ? 'done' : isFailed ? 'pending' : 'pending',
    },
  ];

  return (
    <ol className="workflow-steps" aria-label="Pipeline de traitement">
      {steps.map((s, i) => (
        <li key={s.key} className={`step state-${s.state}`}>
          <span className="step-marker" aria-hidden>
            {s.state === 'done' ? <Icon name="check" size={14} />
              : s.state === 'failed' ? <Icon name="x" size={14} />
              : s.state === 'active' ? <span className="step-spinner" />
              : <span className="step-dot" />}
          </span>
          <span className="step-body">
            <span className="step-num">Étape {i + 1}</span>
            <span className="step-label">
              <Icon name={s.icon} size={14} /> {s.label}
            </span>
            <span className="step-sub" title={s.sub}>{s.sub}</span>
          </span>
          {i < steps.length - 1 && <span className="step-connector" aria-hidden />}
        </li>
      ))}
    </ol>
  );
}

/** AI verification audit. Renders nothing if the audit hasn't been performed
 * yet (legacy invoices) or if no issues were found. Otherwise shows a sorted
 * list of issues with severity dots. */
function VerificationCard({ verification }: { verification?: VerificationResult }) {
  if (!verification || !verification.verifiedAt) return null;
  const issues = verification.issues ?? [];
  if (issues.length === 0) {
    return (
      <Alert tone="success" title="Vérification IA réussie">
        Le modèle d'audit {verification.model || 'IA'} a contrôlé l'extraction et n'a relevé aucune anomalie.
      </Alert>
    );
  }
  const sorted = [...issues].sort((a, b) => {
    const rank = { high: 0, medium: 1, low: 2 } as const;
    return rank[a.severity] - rank[b.severity];
  });
  const highest = sorted[0].severity;
  const tone = highest === 'high' ? 'danger' : highest === 'medium' ? 'warning' : 'info';
  return (
    <Alert tone={tone} title={`L'audit IA a signalé ${issues.length} anomalie${issues.length > 1 ? 's' : ''}`}>
      <ul className="verify-list">
        {sorted.map((it, idx) => (
          <li key={`${it.field}-${idx}`} className={`verify-row verify-${it.severity}`}>
            <span className="verify-dot" aria-hidden />
            <code className="verify-field">{it.field}</code>
            <span className="verify-issue">{it.issue}</span>
          </li>
        ))}
      </ul>
    </Alert>
  );
}

function Section({ title, full, children }: { title: string; full?: boolean; children: React.ReactNode }) {
  return (
    <div style={full ? { gridColumn: '1 / -1' } : undefined}>
      <h3 style={{ fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-4)', margin: '8px 0 6px', fontWeight: 500 }}>{title}</h3>
      {full ? <div className="form-section-full">{children}</div> : <div className="form-grid">{children}</div>}
    </div>
  );
}

const PREVIEW_WIDTH_KEY = 'mi.preview.width';
const PREVIEW_MIN_W = 320;
const PREVIEW_MAX_W = 900;
const PREVIEW_ZOOM_KEY = 'mi.preview.zoom';
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.1;

function FilePreview({ invoice }: { invoice: Invoice }) {
  const isImage = invoice.source.mimeType.startsWith('image/');
  const isPdf = invoice.source.mimeType === 'application/pdf';
  const totals = `${formatMoney(invoice.subtotal, invoice.currency)} + ${formatMoney(invoice.taxTotal, invoice.currency)} = ${formatMoney(invoice.total, invoice.currency)}`;

  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [zoom, setZoom] = useState<number>(() => {
    try {
      const stored = Number(window.localStorage.getItem(PREVIEW_ZOOM_KEY));
      if (Number.isFinite(stored) && stored >= ZOOM_MIN && stored <= ZOOM_MAX) return stored;
    } catch { /* ignore */ }
    return 1;
  });
  const [width, setWidth] = useState<number | null>(() => {
    try {
      const stored = Number(window.localStorage.getItem(PREVIEW_WIDTH_KEY));
      if (Number.isFinite(stored) && stored >= PREVIEW_MIN_W && stored <= PREVIEW_MAX_W) return stored;
    } catch { /* ignore */ }
    return null;
  });
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);
  const paneRef = useRef<HTMLElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Keep the latest zoom in a ref so the imperative wheel handler doesn't
  // need to be rebound (and lose its `passive: false` flag) on every change.
  const zoomRef = useRef(zoom);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);

  useEffect(() => {
    try { window.localStorage.setItem(PREVIEW_ZOOM_KEY, String(zoom)); }
    catch { /* ignore */ }
  }, [zoom]);
  useEffect(() => {
    if (width === null) return;
    try { window.localStorage.setItem(PREVIEW_WIDTH_KEY, String(width)); }
    catch { /* ignore */ }
  }, [width]);

  // Drive the parent `.detail-grid` template via a CSS custom property when the
  // user has set a preferred width. When `width` is null we fall back to the
  // 1.75fr / 1fr default in stylesheet.
  useEffect(() => {
    const grid = paneRef.current?.parentElement;
    if (!grid) return;
    if (width === null) {
      grid.style.removeProperty('grid-template-columns');
    } else {
      grid.style.gridTemplateColumns = `minmax(0, 1fr) ${width}px`;
    }
  }, [width]);

  useEffect(() => {
    let revoked = false;
    let created: string | null = null;
    setObjectUrl(null);
    setPreviewError(null);
    fetchOriginalFileBlob(invoice.id)
      .then((blob) => {
        if (revoked) return;
        created = URL.createObjectURL(blob);
        setObjectUrl(created);
      })
      .catch((e: unknown) => {
        if (revoked) return;
        setPreviewError(e instanceof Error ? e.message : 'Could not load file');
      });
    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [invoice.id]);

  function handleDownload() {
    fetchOriginalFileBlob(invoice.id)
      .then((blob) => downloadBlob(blob, invoice.source.filename))
      .catch((e: unknown) => toast(e instanceof Error ? e.message : 'Download failed', 'danger'));
  }

  const onResizeStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const pane = paneRef.current;
    if (!pane) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startW: pane.getBoundingClientRect().width };
    document.body.classList.add('is-resizing-ew');
  }, []);
  const onResizeMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const s = dragRef.current;
    if (!s) return;
    // Drag-left grows preview, drag-right shrinks. Handle sits on the LEFT edge.
    const next = Math.max(PREVIEW_MIN_W, Math.min(PREVIEW_MAX_W, s.startW - (e.clientX - s.startX)));
    setWidth(next);
  }, []);
  const onResizeEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    document.body.classList.remove('is-resizing-ew');
    try { (e.target as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  }, []);

  const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
  const zoomReset = () => setZoom(1);

  // Ctrl/⌘ + wheel anywhere inside the scroll container zooms (anchored on the
  // cursor); plain wheel scrolls the container normally. Pointer-down with no
  // modifier starts a drag-to-pan, adjusting scrollLeft/scrollTop while held.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !objectUrl || previewError) return undefined;

    function onWheel(e: WheelEvent) {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      if (!el) return;
      const prev = zoomRef.current;
      const direction = e.deltaY < 0 ? 1 : -1;
      const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, +(prev + direction * ZOOM_STEP).toFixed(2)));
      if (next === prev) return;
      // Anchor zoom on the cursor: keep the document point under the cursor
      // pinned by adjusting the scroll offset proportionally.
      const rect = el.getBoundingClientRect();
      const cursorX = e.clientX - rect.left + el.scrollLeft;
      const cursorY = e.clientY - rect.top + el.scrollTop;
      const ratio = next / prev;
      setZoom(next);
      // Defer the scroll correction so the new layout has measured first.
      requestAnimationFrame(() => {
        if (!el) return;
        el.scrollLeft = cursorX * ratio - (e.clientX - rect.left);
        el.scrollTop = cursorY * ratio - (e.clientY - rect.top);
      });
    }

    let panStart: { x: number; y: number; left: number; top: number; id: number } | null = null;
    function onPointerDown(e: PointerEvent) {
      if (!el) return;
      // Ignore right-click, modifier-down (so ctrl+wheel doesn't grab as pan),
      // and touches that should pinch-zoom the page natively.
      if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
      // Don't hijack clicks on interactive children (buttons, links, inputs).
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'A' || tag === 'BUTTON' || tag === 'INPUT' || tag === 'TEXTAREA') return;
      panStart = {
        x: e.clientX,
        y: e.clientY,
        left: el.scrollLeft,
        top: el.scrollTop,
        id: e.pointerId,
      };
      el.setPointerCapture(e.pointerId);
      el.classList.add('panning');
    }
    function onPointerMove(e: PointerEvent) {
      if (!panStart || !el) return;
      el.scrollLeft = panStart.left - (e.clientX - panStart.x);
      el.scrollTop = panStart.top - (e.clientY - panStart.y);
    }
    function onPointerUp(e: PointerEvent) {
      if (!panStart || !el) return;
      try { el.releasePointerCapture(panStart.id); } catch { /* ignore */ }
      panStart = null;
      el.classList.remove('panning');
      void e;
    }

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);

    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
    };
  }, [objectUrl, previewError, isPdf, isImage]);

  const showZoom = !previewError && objectUrl && (isPdf || isImage);
  // For images, scaling via natural width preserves source pixels — only goes
  // fuzzy past 100% when the source has finite resolution. For PDFs we use a
  // canvas renderer that redraws at the new zoom (see PdfCanvas), so the
  // result is always crisp regardless of zoom level.
  const imgStyle: React.CSSProperties = {
    width: `${Math.round(zoom * 100)}%`,
    height: 'auto',
    transition: 'width 0.12s ease',
    imageRendering: zoom >= 1 ? 'auto' : 'auto',
  };

  return (
    <aside className="preview-pane" ref={paneRef as React.RefObject<HTMLElement>}>
      <div
        className="preview-resize-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label="Redimensionner l'aperçu"
        onPointerDown={onResizeStart}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeEnd}
        onPointerCancel={onResizeEnd}
        onDoubleClick={() => setWidth(null)}
        title="Glisser pour redimensionner · double-clic pour réinitialiser"
      />
      <MacTitlebar title={invoice.source.filename} />
      {showZoom && (
        <div className="preview-toolbar">
          <button type="button" className="zoom-btn" aria-label="Zoom arrière" onClick={zoomOut} disabled={zoom <= ZOOM_MIN}>−</button>
          <button type="button" className="zoom-level" onClick={zoomReset} title="Réinitialiser le zoom">{Math.round(zoom * 100)}%</button>
          <button type="button" className="zoom-btn" aria-label="Zoom avant" onClick={zoomIn} disabled={zoom >= ZOOM_MAX}>+</button>
        </div>
      )}
      <div className="body">
        {previewError ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <p className="muted">{previewError}</p>
          </div>
        ) : !objectUrl ? (
          <div style={{ padding: 24, textAlign: 'center' }} className="muted">Chargement de l'aperçu…</div>
        ) : isPdf ? (
          <div className="preview-scroll pdf" ref={scrollRef}>
            <PdfCanvas src={objectUrl} zoom={zoom} />
          </div>
        ) : isImage ? (
          <div className="preview-scroll" ref={scrollRef}>
            <img alt={invoice.source.filename} src={objectUrl} style={imgStyle} draggable={false} />
          </div>
        ) : (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <p className="muted">Aperçu intégré non disponible pour ce format.</p>
            <p className="mono" style={{ fontSize: 13 }}>{totals}</p>
            <Button variant="secondary" size="sm" onClick={handleDownload} leadingIcon={<Icon name="download" size={14} />}>
              Télécharger l'original
            </Button>
          </div>
        )}
      </div>
    </aside>
  );
}

function ExportModal({ open, onClose, invoiceId }: { open: boolean; onClose: () => void; invoiceId: string }) {
  const [selected, setSelected] = useState<Record<ExportFormat, boolean>>({
    json: true, csv: false, xlsx: false, pdf: false, xml: false, ubl: false, facturx: false, txt: false,
  });
  const [busy, setBusy] = useState(false);

  const list = useMemo(() => (Object.entries(selected).filter(([, v]) => v).map(([k]) => k as ExportFormat)), [selected]);

  async function run() {
    if (list.length === 0) return;
    setBusy(true);
    try {
      if (list.length === 1) {
        const fmt = list[0];
        const blob = await exportInvoice(invoiceId, fmt);
        const ext = fmt === 'facturx' ? 'pdf' : fmt === 'ubl' ? 'xml' : fmt;
        downloadBlob(blob, `facture-${invoiceId}.${ext}`);
      } else {
        const blob = await exportInvoicesZip(invoiceId, list);
        downloadBlob(blob, `factures-${invoiceId}.zip`);
      }
      toast('Exportation commencée.', 'success');
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Échec de l\'exportation', 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Exporter la facture"
      lede="Sélectionnez un ou plusieurs formats. Plusieurs formats seront téléchargés dans une archive ZIP unique."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{en.common.cancel}</Button>
          <Button variant="primary" onClick={run} disabled={busy || list.length === 0}>
            {busy ? '…' : `Exporter${list.length > 1 ? ` (${list.length})` : ''}`}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ marginTop: 8 }}>
        {EXPORT_FORMATS.map((f) => (
          <Checkbox
            key={f.key}
            checked={selected[f.key]}
            onChange={(e) => setSelected((s) => ({ ...s, [f.key]: e.target.checked }))}
            label={
              <span>
                <strong>{f.label}</strong> <span className="muted" style={{ fontSize: 12 }}>· {f.desc}</span>
              </span>
            }
          />
        ))}
      </div>
    </Modal>
  );
}
